import {
    Button,
    Card,
    Col,
    message,
    Row,
    Anchor,
    Form,
    Input,
    InputNumber,
    Upload,
    DatePicker,
    Modal, Select, Switch
} from "antd";
import React, {useEffect, useRef, useState} from "react";
import { downloadRenderFile, openS3File, uploadFile } from "../../api/files";
import { apiDelete, parseJsonResponse } from "../../api/apiClient";
import { setInitialUploadForms, updateDataContent } from "../../utils/formUtils";
import { dayjsToISO } from "../../utils/dateFormat";
import { LoadingScreen } from "../ui/LoadingScreen";
import { SaveIcon } from "../ui/SaveIcon";
import { MyInputNumber } from "../ui/MyInputNumber";
import { useModals } from "../../hooks/useModals";
import { useGutachtenPersist } from "../../hooks/useGutachtenPersist";
import { useGutachtenData } from "../../hooks/useGutachtenData";
import { useParams } from "react-router-dom";
import { DeleteOutlined, DownloadOutlined, ExportOutlined, FileTextOutlined, UploadOutlined } from "@ant-design/icons";
import { AutoComplete } from "antd";
import { RiDeleteBin5Fill } from "react-icons/ri";
import TextArea from "antd/es/input/TextArea";
import { useSwitch } from '../../pages/SwitchContext';
import GutachtenShell from "./GutachtenShell";

const { Option } = Select;


const MENU_LINKS = [
    { href: '#organisatorisches', title: 'Organisatorisches' },
    { href: '#grundbuch-kataster', title: 'Grundbuch und Kataster' },
    { href: '#sondereigentum', title: 'Sondereigentum' },
    { href: '#ortstermin', title: 'Ortstermin' },
    { href: '#unterlagen', title: 'Unterlagen' },
    { href: '#hinweise', title: 'Hinweise' },
];

const unterlagenHasDraft = (forms) =>
    !!(forms.Unterlagen && Object.prototype.hasOwnProperty.call(forms.Unterlagen, 'undefined'));

const ortsterminHasOpenDraftRow = (ortCards) =>
    (ortCards || []).some((e) => e != null && (e.id === undefined || e.id === null));

const FormContainer = () => {
    const [cards, setCards] = useState({});
    const [formFormular1] = Form.useForm();
    const inputRefs = useRef({});
    const currentFileChangedRef = useRef(null);
    const currentFileChangedIDRef = useRef(null);
    const currentFileChangedComponentRef = useRef(null);
    const handleRef = (name, element) => {
        if (element) inputRefs.current[name] = element;
    };
    const { auftragID, gutachtenID, tab } = useParams();
    const [selectedOption, setSelectedOption] = useState(null);
    const { isSwitchOn } = useSwitch();
    const { saveIcon, saveIcon1, isLoading, setIsLoading, updateTrigger, setUpdateTrigger, updateGutachten } = useGutachtenPersist(gutachtenID);

    const [uploadForms, setUploadForms] = useState({});
    const { modals, handleOpenTextbausteine, handleCloseTextbausteine, renderModals, selectedTextBausteine } = useModals();

    const uploadFormsRef = useRef(uploadForms);
    const cardsRef = useRef(cards);
    useEffect(() => {
        uploadFormsRef.current = uploadForms;
    }, [uploadForms]);
    useEffect(() => {
        cardsRef.current = cards;
    }, [cards]);

    const { dataContentRef, initialValues, enumerationValues } = useGutachtenData(gutachtenID, {
        setIsLoading,
        updateTrigger,
        saveIcon,
        onLoad: (data) => {
            const prevForms = uploadFormsRef.current;
            const prevCards = cardsRef.current;

            const mergedAllgAng = { ...data.AllgAng };
            const serverUnter = Array.isArray(data.AllgAng.Unterlagen) ? [...data.AllgAng.Unterlagen] : [];
            const localUnter = Array.isArray(prevCards.Unterlagen) ? prevCards.Unterlagen : [];

            const forms = {};
            setInitialUploadForms(forms, data.AllgAng.Unterlagen, 'Unterlagen', false);

            const extras = Math.max(0, localUnter.length - serverUnter.length);
            if (extras > 0) {
                for (let i = 0; i < extras; i++) {
                    serverUnter.push({});
                }
                const draftSlot =
                    prevForms.Unterlagen?.undefined ??
                    { file: '', visible: true };
                forms.Unterlagen = { ...forms.Unterlagen, undefined: { ...draftSlot } };
            }

            mergedAllgAng.Unterlagen = serverUnter;
            setCards(mergedAllgAng);
            setUploadForms(forms);
        },
    });

    const handleSelectChange = (value) => setSelectedOption(value);

    const triggerFocus = (name) => {
        if (inputRefs.current[name]) inputRefs.current[name].focus();
    };
    useEffect(() => {
        if (selectedTextBausteine) {
            formFormular1.setFieldsValue({ [modals[modals.length - 1].field]: selectedTextBausteine });
            triggerFocus(modals[modals.length - 1].field);
        }
    }, [selectedTextBausteine]);

    /**
     * Generates a new card and adds it to the specified component.
     *
     * @param {string} component - The component to add the card to.
                            * @param {boolean} [uploadForm=false] - Indicates whether an upload form should be created for the new card.
                            */
    const addCard = (component, uploadForm = false) => {
        if (uploadForm) {
            setUploadForms((prev) => {
                const next = { ...prev };
                const bucket = { ...(next[component] || {}) };
                bucket.undefined = { file: '', visible: true };
                next[component] = bucket;
                return next;
            });
        }

        setCards((prev) => ({
            ...prev,
            [component]: [...(prev[component] || []), {}],
        }));
    };
    /**
     * Deletes a card from the specified component at the given index with the provided ID.
     *
     * @param {string} component - The component from which the card will be deleted.
                            * @param {number} index - The index of the card in the component.
                            * @param {string} id - The ID of the card to be deleted.
                            */
    const deleteCard = (component, index, id) => {
        Modal.confirm({
            title: 'Komponente löschen',
            content: 'Möchten Sie diesen Eintrag wirklich löschen?',
            onOk() {
                deleteCardConfirmed(component, index, id);
            },
            onCancel() {
                console.log('Löschung abgebrochen');
            },
        });
    };

    /**
     * Deletes a card from the specified component.
     *
     * @param {string} component - The name of the component.
                            * @param {number} index - The index of the card to be deleted.
                            * @param {string} id - The ID of the card to be deleted.
                            */
    const deleteCardConfirmed = (component, index, id) => {
        const currentComponent = dataContentRef.current.AllgAng[component][index];
        for (const key in currentComponent) {
            if (currentComponent.hasOwnProperty(key)) {
                if(typeof currentComponent[key] == "object" && currentComponent[key] != null  && currentComponent[key].id){
                    handleRemoveAttachmentConfirmed("", currentComponent[key].id).then();
                }
            }
        }
        const updatedCardsComponent = [...(cards[component] || [])];
        updatedCardsComponent.splice(index, 1);
        setCards((prev) => ({ ...prev, [component]: updatedCardsComponent }));
        updateDataContent(component, index, dataContentRef.current, "deleteComponent");
        updateGutachten(dataContentRef.current, false);
    };
    const customUploadFile = async (file) => {

        try {
            if (file) {
                const responseUF = await uploadFile(file, false, auftragID);

                // Check if the response is an array
                const fileValue = {};
                if (Array.isArray(responseUF)) {
                    responseUF.forEach((item) => {
                        fileValue["url"] = item.hash + item.ext;
                        fileValue["id"] = item.id;
                        fileValue["name"] = item.name;
                    });
                }

                updateDataContent(currentFileChangedRef.current, fileValue, dataContentRef.current, "updateByID");
                updateGutachten(dataContentRef.current, true, false);
            }
        } catch (error) {
            console.error(error);
        }
    };

    /* function setInitialUploadForms(files,name){
                                let file_container;
                            updatedUploadForms[name] = { };
                            for (const key in files) {
             if (files.hasOwnProperty(key)) {
                 const file = files[key];
                            console.log(file);
                            for(const key_file in file){
                     if (file.hasOwnProperty(key_file)) {
                         const value = file[key_file];

                            if(typeof value === "object" && value !== null){
                                file_container = value;
                         }
                     }
                 }
                            updatedUploadForms[name][file.id] = { }
                            if(file_container){
                                updatedUploadForms[name][file.id].file = file_container;
                            updatedUploadForms[name][file.id].visible = false;
                 } else {
                                updatedUploadForms[name][file.id].file = "";
                            updatedUploadForms[name][file.id].visible = true;
                 }
                            file_container = undefined;
             }
         }
                            setUploadForms(updatedUploadForms);
     }*/
    const handleRemoveAttachment = (dataContentEntry, file) => {
        Modal.confirm({
            title: 'Datei löschen',
            content: 'Möchten Sie die Datei wirklich löschen?',
            onOk() {
                handleRemoveAttachmentConfirmed(dataContentEntry, file);
            },
            onCancel() {
                console.log('Löschung abgebrochen');
            },
        });
    };

    const handleRemoveAttachmentConfirmed = async (dataContentEntry, file) => {
        try {
            const response = await apiDelete(`/helper/acl/upload/${file}`);

            if (response.ok) {
                await parseJsonResponse(response);


            } else {
                console.log(`Request failed with status ${response.status}`);
            }
        } catch (error) {
            return error;
        }
        if(dataContentEntry != "") {
            updateDataContent(dataContentEntry, "-", dataContentRef.current, "updateByID");
            updateGutachten(dataContentRef.current, true, false);
        }
    }

    const FileChanged = (name, id, component) => {
        currentFileChangedRef.current = name;
        currentFileChangedIDRef.current = id;
        currentFileChangedComponentRef.current = component;
    };
    useEffect(() => formFormular1.resetFields(), [initialValues]);
    return (
        <div className={`${isSwitchOn ? 'bg-black' : 'bg-white'} sm:p-[20px] p-[10px] rounded-[30px]`}>
            {isLoading && <LoadingScreen />}
            {saveIcon1 && <SaveIcon saveIcon={saveIcon} />}
            {renderModals()}
            <h2 className={`${isSwitchOn ? 'text-white' : 'text-black'} font-bold text-16px  md:text-[28px] mb-[20px] generalText`}>Allgemeine Angaben</h2>
            <div>
                {dataContentRef.current && initialValues && enumerationValues && ( // Bedingung überprüft, ob dataContentRef.current bereits vorhanden ist
                    <Form
                        form={formFormular1}
                        onValuesChange={(changedValues) => {
                            const updateByIDArray = ["AllgAng__Ortstermin", "AllgAng__Unterlagen"];
                            Object.entries(changedValues).forEach(([fieldName, value]) => {
                                const isoDate = dayjsToISO(value);
                                if (isoDate !== null || (value === null && fieldName)) {
                                    const saveValue = isoDate;
                                    if (updateByIDArray.some(v => fieldName.includes(v))) {
                                        updateDataContent(fieldName, saveValue, dataContentRef.current, "updateByID");
                                    } else {
                                        updateDataContent(fieldName, saveValue, dataContentRef.current, "updateByIndex");
                                    }
                                    updateGutachten(dataContentRef.current, false);
                                }
                            });
                        }}
                        onBlur={(e) => {
                            if (e.target.type !== "button" || e.target.role == "switch") {
                                if (e.target.closest?.('.ant-picker')) return;
                                if (e.target.id === "" || !e.target.id) return;
                                const updateValue = e.target.type === "search"
                                    ? (setSelectedOption(null), selectedOption)
                                    : e.target.role === "switch"
                                        ? e.target.getAttribute('aria-checked') === 'true'
                                        : e.target.value;
                                const updateByIDArray = ["AllgAng__Ortstermin", "AllgAng__Unterlagen"];
                                if (updateByIDArray.some(value => e.target.id.includes(value))) {
                                    updateDataContent(e.target.id, updateValue, dataContentRef.current, "updateByID");
                                } else {
                                    updateDataContent(e.target.id, updateValue, dataContentRef.current, "updateByIndex");
                                }
                                if (e.target.id.includes("undefined")) {
                                    updateGutachten(dataContentRef.current, true, false);
                                } else {
                                    updateGutachten(dataContentRef.current, false);
                                }
                            }
                        }}
                        initialValues={initialValues}
                        labelCol={{span: 8}}
                        wrapperCol={{span: 16}}
                        layout="horizontal"
                        // style={{ maxWidth: 800 }}
                    >
                        <div id="organisatorisches" className="scrollable-section">
                            <h3 className={`font-bold text-16px mb-[12px]  md:text-[20px] generalText ${isSwitchOn ? 'text-white' : 'text-black'}`}>Organisatorisches</h3>
                            <div className={`allgemeineClass ${isSwitchOn ? 'maxColor' : ''} `}>
                                <Form.Item label="Gutachtenart" name='AllgAng__AllgAng_Detail__GA_ArtV'>
                                    <AutoComplete
                                        className={"AutoCompleteTextArea"}
                                        options={enumerationValues.sub_com_GA_Art?.map(
                                            (option, index) => ({value: option, key: index})
                                        )}
                                        filterOption={(inputValue, option) =>
                                            option.value
                                                .toUpperCase()
                                                .indexOf(inputValue.toUpperCase()) !== -1
                                        }
                                        onChange={handleSelectChange} // Add your change handler if needed
                                    >
                                        <TextArea
                                            placeholder="Gutachtenart"
                                            autoSize={true}
                                            className="textInput"
                                        />
                                    </AutoComplete>

                                </Form.Item>
                                <Form.Item label="Kurzgutachten" name="AllgAng__AllgAng_Detail__GA_KGA">
                                    <span
                                        className={`mr-[10px] ${isSwitchOn ? 'text-white' : 'text-black'}`}>Nein</span>
                                    <Switch id="AllgAng__AllgAng_Detail__GA_KGA" className=""
                                            defaultChecked={initialValues.AllgAng__AllgAng_Detail__GA_KGA}/><span
                                    className={` ml-[10px] ${isSwitchOn ? 'text-white' : 'text-black'}`}>Ja</span>
                                </Form.Item>
                            </div>

                            <div className={`allgemeineClass ${isSwitchOn ? 'maxColor' : ''} `}>

                                <Form.Item label="Objektart" name='AllgAng__AllgAng_Detail__ObjektartV'>
                                    <AutoComplete
                                        className={"AutoCompleteTextArea"}
                                        options={enumerationValues.sub_com_Objektart?.map(
                                            (option, index) => ({value: option, key: index})
                                        )}
                                        filterOption={(inputValue, option) =>
                                            option.value
                                                .toUpperCase()
                                                .indexOf(inputValue.toUpperCase()) !== -1
                                        }
                                        onChange={handleSelectChange} // Add your change handler if needed
                                    >
                                        <TextArea
                                            placeholder="Objektart"
                                            autoSize={true}
                                            className="textInput"
                                        />
                                    </AutoComplete>

                                </Form.Item>
                                <Form.Item label="Straße" name="AllgAng__AllgAng_Detail__Strasse">
                                    <Input className="inputsAll"/>
                                </Form.Item>
                                <Form.Item label="Nr / Zusatz" name="AllgAng__AllgAng_Detail__NrZusatz">
                                    <Input className="inputsAll"/>
                                </Form.Item>
                                <Form.Item label="PLZ" name="AllgAng__AllgAng_Detail__PLZ">
                                    <Input className="inputsAll"/>
                                </Form.Item>
                                <Form.Item label="Ort" name="AllgAng__AllgAng_Detail__Ort">
                                    <Input className="inputsAll"/>
                                </Form.Item>
                                <Form.Item label="Ortsteil" name="AllgAng__AllgAng_Detail__Ortsteil">
                                    <Input className="inputsAll"/>
                                </Form.Item>
                                <Form.Item label={`Wertermittlungsstichtag`}>
                                    <Form.Item name={`AllgAng__AllgAng_Detail__GA_Wertermittlungsstichtag`} noStyle>
                                        <DatePicker format={"DD.MM.YYYY"} className="inputsAll"/>
                                    </Form.Item>
                                </Form.Item>
                                <Form.Item label={`Qualitätsstichtag`}>
                                    <Form.Item name={`AllgAng__AllgAng_Detail__GA_Qualistichtag`} noStyle>
                                        <DatePicker format={"DD.MM.YYYY"} className="inputsAll"/>
                                    </Form.Item>
                                </Form.Item>
                                <Form.Item label={`Tag der Ortsbesichtigung`}>
                                    <Form.Item name={`AllgAng__AllgAng_Detail__GA_TagOB`} noStyle>
                                        <DatePicker format={"DD.MM.YYYY"} className="inputsAll"/>
                                    </Form.Item>
                                </Form.Item>
                            </div>

                            <div className={`flex textOuter justify-start gap-[10px] ${isSwitchOn ? 'maxColor' : ''}`}>
                                <Form.Item label={`Ortstermin Zusammenfassung:`}
                                           name={`AllgAng__AllgAng_Detail__OT_Zusammenfassung`}>
                                    <TextArea autoSize={true} className="textInput"/>
                                </Form.Item>
                                <Button className="mt-[35px] md:mt-0 md:ml-[13px] ml-[20px] text-[#FF2E3B] fileIcon"
                                        htmlType="button" onMouseDown={(e) => e.preventDefault()} onClick={() => handleOpenTextbausteine('AllgAng__AllgAng_Detail__OT_Zusammenfassung', 'Ortstermin Zusammenfassung', 'Ortstermin')}><FileTextOutlined/></Button>
                            </div>
                            <div className={`allgemeineClass mt-[20px] ${isSwitchOn ? 'maxColor' : ''}`}>
                                <Form.Item label="Ausfertigungen" name="AllgAng__AllgAng_Detail__GA_Ausfertigungen">
                                    <MyInputNumber className="selectInput"/>
                                </Form.Item>
                                <Form.Item label="Zweck" name="AllgAng__AllgAng_Detail__GA_ZweckV">
                                    <AutoComplete
                                        className={"AutoCompleteTextArea"}
                                        options={enumerationValues.sub_com_GA_Zweck?.map(
                                            (option, index) => ({value: option, key: index})
                                        )}
                                        filterOption={(inputValue, option) =>
                                            option.value
                                                .toUpperCase()
                                                .indexOf(inputValue.toUpperCase()) !== -1
                                        }
                                        onChange={handleSelectChange} // Add your change handler if needed
                                    >
                                        <TextArea
                                            placeholder="Zweck"
                                            autoSize={true}
                                            className="textInput"
                                        />
                                    </AutoComplete>

                                </Form.Item>
                            </div>

                            <div className={`flex textOuter justify-start gap-[10px] ${isSwitchOn ? 'maxColor' : ''}`}>
                                <Form.Item label="Allgemeine Beschreibung"
                                           name="AllgAng__AllgAng_Detail__GA_Beschreibung">
                                    <TextArea autoSize={true} className="textInput"
                                              ref={(element) => handleRef('AllgAng__AllgAng_Detail__GA_Beschreibung', element)}/>
                                </Form.Item>
                                <Button className="mt-[35px] md:mt-0 md:ml-[13px] ml-[20px] text-[#FF2E3B] fileIcon"
                                        htmlType="button" onMouseDown={(e) => e.preventDefault()} onClick={() => handleOpenTextbausteine('AllgAng__AllgAng_Detail__GA_Beschreibung', 'Allgemeine Beschreibung', 'Auftragsbeschreibung')}><FileTextOutlined/></Button>

                            </div>
                            <div className={`allgemeineClass mt-[20px] ${isSwitchOn ? 'maxColor' : ''}`}>
                                <Form.Item label="Vereinbartes Fertigstellungsdatum">
                                    <Form.Item name={`AllgAng__AllgAng_Detail__GA_Datum_Vereinbart`} noStyle>
                                        <DatePicker format={"DD.MM.YYYY"} className="inputsAll"/>
                                    </Form.Item>
                                </Form.Item>
                            </div>
                            <div className={`allgemeineClass mt-[20px] ${isSwitchOn ? 'maxColor' : ''}`}>
                                <Form.Item label="Tatsächliches Fertigstellungsdatum">
                                    <Form.Item name={`AllgAng__AllgAng_Detail__GA_Datum`} noStyle>
                                        <DatePicker format={"DD.MM.YYYY"} className="inputsAll"/>
                                    </Form.Item>
                                </Form.Item>
                            </div>
                        </div>
                        <div id="grundbuch-kataster" className="scrollable-section">
                            <h3 className={`font-bold text-16px mb-[12px]  md:text-[20px] generalText ${isSwitchOn ? 'text-white' : 'text-black'}`}>Grundbuch
                                & Kataster</h3>
                            <div className={`allgemeineClass ${isSwitchOn ? 'maxColor' : ''} `}>
                                <Form.Item label="Amtsgericht" name='AllgAng__GB_Amtsgericht'>
                                    <Input className="inputsAll"/>
                                </Form.Item>
                                <Form.Item label="Bezirk" name='AllgAng__GB_Bezirk'>
                                    <Input className="inputsAll"/>
                                </Form.Item>
                                <Form.Item label="Gemarkung" name='AllgAng__GB_Gemarkung'>
                                    <Input className="inputsAll"/>
                                </Form.Item>
                                <Form.Item label="Blattnummer" name='AllgAng__GB_Blatt'>
                                    <Input className="inputsAll"/>
                                </Form.Item>
                                <Form.Item label="Laufende Nummer" name='AllgAng__GB_laufNr'>
                                    <Input className="inputsAll"/>
                                </Form.Item>
                                <Form.Item label="Gemarkung" name='AllgAng__KA_Gemarkung'>
                                    <Input className="inputsAll"/>
                                </Form.Item>
                                <Form.Item label="Flur" name='AllgAng__KA_Flur'>
                                    <Input className="inputsAll"/>
                                </Form.Item>
                                <Form.Item label="Flurstücke" name='AllgAng__KA_Flurstueck'>
                                    <Input className="inputsAll"/>
                                </Form.Item>
                            </div>
                        </div>
                        <div id="sondereigentum" className="scrollable-section">
                            <h3 className={`font-bold text-16px mb-[12px]  md:text-[20px] generalText ${isSwitchOn ? 'text-white' : 'text-black'}`}>Sondereigentum</h3>
                            <div className={`allgemeineClass ${isSwitchOn ? 'maxColor' : ''} `}>
                                <Form.Item label="Mieteigentumsanteil" name='AllgAng__SE_MEA'>
                                    <Input className="inputsAll"/>
                                </Form.Item>
                                <Form.Item label="Mieteigentumsanteil gesamt" name='AllgAng__SE_MEA_ges'>
                                    <MyInputNumber className="inputsAll"/>
                                </Form.Item>
                                <Form.Item label="Objektart" name='AllgAng__SE_ObjektartV'>
                                    <AutoComplete
                                        className={"AutoCompleteTextArea"}
                                        options={enumerationValues.com_SE_Objektart?.map(
                                            (option, index) => ({value: option, key: index})
                                        )}
                                        filterOption={(inputValue, option) =>
                                            option.value
                                                .toUpperCase()
                                                .indexOf(inputValue.toUpperCase()) !== -1
                                        }
                                        onChange={handleSelectChange} // Add your change handler if needed
                                    >
                                        <TextArea
                                            placeholder="Objektart"
                                            autoSize={true}
                                            className="textInput"
                                        />
                                    </AutoComplete>

                                </Form.Item>
                                <Form.Item label="Anschriften" name='AllgAng__SE_Anschriften'>
                                    <Input className="inputsAll"/>
                                </Form.Item>
                                <Form.Item label="Lage" name='AllgAng__SE_Lage'>
                                    <Input className="inputsAll"/>
                                </Form.Item>
                                <Form.Item label="Bezeichnung" name='AllgAng__SE_BEzeichnung'>
                                    <Input className="inputsAll"/>
                                </Form.Item>
                                <Form.Item label="Nebenräume" name='AllgAng__SE_Nebenraeume'>
                                    <Input className="inputsAll"/>
                                </Form.Item>
                                <Form.Item label="Sondernutzungsrecht" name='AllgAng__SE_SNR'>
                                    <Input className="inputsAll"/>
                                </Form.Item>
                            </div>
                        </div>

                        <div id="ortstermin" className="scrollable-section border-t border-gray-300">
                            <div
                                className="flex justify-between items-center p-[10px]  md:p-[20px] pr-[3%] md:pr-[10%]">
                                <h3 className={`font-bold text-16px  md:text-[20px] generalText ${isSwitchOn ? 'text-white' : 'text-black'}`}>Teilnehmer
                                    Ortstermin</h3>

                            </div>
                            {cards.Ortstermin?.map((entry, index) => (
                                <Card className={`${isSwitchOn ? 'bgdark' : 'bgwhite'}`} size="small" key={index}>
                                    <div
                                        className={`flex justify-center  sacherCenterText ${isSwitchOn ? 'maxColor' : ''}`}>
                                        <Form.Item label={`Name Teilnehmer:`}
                                                   name={`AllgAng__Ortstermin__${entry.id}__NameTeilnehmer`}>
                                            <Input className="inputsAll"/>
                                        </Form.Item>
                                        <div className="ml-[10px] gap-[10px] flex">
                                            <div style={{cursor: 'pointer'}}
                                                 onClick={() => deleteCard("Ortstermin", index, entry.id)}>
                                                <RiDeleteBin5Fill
                                                    className="text-[#FF2E3B] w-[18px] h-[20px] mt-[40px] sm:mt-0"/>
                                            </div>
                                        </div>
                                    </div>

                                    <div
                                        className={`flex  justify-center sacherCenter1 ${isSwitchOn ? 'maxColor' : ''}`}>
                                        <Form.Item label={`Kategorie Teilnehmer:`}
                                                   name={`AllgAng__Ortstermin__${entry.id}__TeilnehmerArtV`}>
                                            <AutoComplete
                                                className={"AutoCompleteTextArea AutoCompleteTextAreaSmall"}
                                                options={enumerationValues.sub_com_TeilnehmerArt?.map(
                                                    (option, index) => ({value: option, key: index})
                                                )}
                                                filterOption={(inputValue, option) =>
                                                    option.value
                                                        .toUpperCase()
                                                        .indexOf(inputValue.toUpperCase()) !== -1
                                                }
                                                onChange={handleSelectChange} // Add your change handler if needed
                                            >
                                                <TextArea
                                                    placeholder="Kategorie Teilnehmer"
                                                    autoSize={true}
                                                    className="textInput"
                                                />
                                            </AutoComplete>

                                        </Form.Item>
                                    </div>
                                </Card>
                            ))}
                            {!ortsterminHasOpenDraftRow(cards.Ortstermin) && (
                            <div className={`flex  justify-center sacherCenter1 ${isSwitchOn ? 'maxColor' : ''}`}>
                                <Button
                                    className={` ${isSwitchOn ? 'auftragBtn1' : 'auftragBtn'}  text-[10px] md:text-[14px] m-[20px] button_new_component rounded-[30px] border-none`}
                                    onClick={() => addCard("Ortstermin")} id={"button_new_component_Ortstermin"}>Ortstermin
                                    Teilnehmer hinzufügen</Button>
                            </div>
                            )}
                        </div>
                        <div id="unterlagen" className="scrollable-section  border-gray-300">
                            <div className="flex justify-between items-center p-[20px] pr-[10%]">
                                <h3 className={`font-bold text-[20px] generalText ${isSwitchOn ? 'text-white' : 'text-black'}`}>Unterlagen</h3>

                            </div>

                            {cards.Unterlagen?.map((entry, index) => (
                                <Card className={` ${isSwitchOn ? 'bgdark' : 'bgwhite'}`} size="small" key={index}>
                                    <div className="flex md:justify-center gap-[20px] justify-start">
                                        <div
                                            className={`${isSwitchOn ? 'maxColor' : ''} flex justify-start md:justify-center sacherCenterText1`}>
                                            <Form.Item label={`Auswahl`}
                                                       name={`AllgAng__Unterlagen__${entry.id}__UN_AuswahlV`}>
                                                <AutoComplete
                                                    className={"AutoCompleteTextArea AutoCompleteTextAreaSmall"}
                                                    options={enumerationValues.sub_com_UN_Auswahl?.map(
                                                        (option, index) => ({value: option, key: index})
                                                    )}
                                                    filterOption={(inputValue, option) =>
                                                        option.value
                                                            .toUpperCase()
                                                            .indexOf(inputValue.toUpperCase()) !== -1
                                                    }
                                                    onChange={handleSelectChange} // Add your change handler if needed
                                                >
                                                    <TextArea
                                                        placeholder="Auswahl"
                                                        autoSize={true}
                                                        className="textInput"
                                                    />
                                                </AutoComplete>

                                            </Form.Item>
                                            <div style={{marginLeft: "20px", cursor: 'pointer'}}
                                                 onClick={() => deleteCard("Unterlagen", index, entry.id)}>
                                                <RiDeleteBin5Fill
                                                    className="text-[#FF2E3B] w-[18px] h-[20px] mt-40px sm:mt-0"/>
                                            </div>
                                        </div>
                                    </div>
                                    <div
                                        className={`flex-col  justify-center sacherCenter1 ${isSwitchOn ? 'maxColor' : ''}`}>
                                        <Form.Item label={`Anforderer/Übermittler`}
                                                   name={`AllgAng__Unterlagen__${entry.id}__UN_StakeholderV`}>
                                            <AutoComplete
                                                className={"AutoCompleteTextArea AutoCompleteTextAreaSmall"}
                                                options={enumerationValues.sub_com_UN_Stakeholder?.map(
                                                    (option, index) => ({value: option, key: index})
                                                )}
                                                filterOption={(inputValue, option) =>
                                                    option.value
                                                        .toUpperCase()
                                                        .indexOf(inputValue.toUpperCase()) !== -1
                                                }
                                                onChange={handleSelectChange} // Add your change handler if needed
                                            >
                                                <TextArea
                                                    placeholder="Anforderer/Übermittler"
                                                    autoSize={true}
                                                    className="textInput"
                                                />
                                            </AutoComplete>

                                        </Form.Item>
                                        <Form.Item label="Quelle" name={`AllgAng__Unterlagen__${entry.id}__UN_Quelle`}>
                                            <Input className="inputsAll"/>
                                        </Form.Item>

                                        <Form.Item label={`Datum Versand`}>
                                            <Form.Item name={`AllgAng__Unterlagen__${entry.id}__UN_Date_Versand`}
                                                       noStyle>
                                                <DatePicker format={"DD.MM.YYYY"} className="inputsAll"/>
                                            </Form.Item>
                                        </Form.Item>
                                        <Form.Item label={`Datum Erhalt`}>
                                            <Form.Item name={`AllgAng__Unterlagen__${entry.id}__UN_Date_Erhalt`}
                                                       noStyle>
                                                <DatePicker format={"DD.MM.YYYY"} className="inputsAll"/>
                                            </Form.Item>
                                        </Form.Item>
                                    </div>

                                    <Form.Item label="Dokument" name={`AllgAng__Unterlagen__${entry.id}__UN_Doc`}
                                               className={`${isSwitchOn ? 'maxColor' : ''} max-content3`}>
                                        {uploadForms.Unterlagen?.[entry.id]?.visible ? (
                                            <>
                                                {dataContentRef.current.AllgAng.Unterlagen[index] && dataContentRef.current.AllgAng.Unterlagen[index].UN_AuswahlV && (
                                                    <Button
                                                        icon={<DownloadOutlined/>}
                                                        onClick={() => downloadRenderFile("Anforderung", "docx", auftragID, dataContentRef.current.AllgAng.Unterlagen[index].UN_AuswahlV)}
                                                        style={{cursor: 'pointer'}}
                                                    >
                                                        Vorlage Anforderung herunterladen
                                                    </Button>
                                                )}
                                                <Upload.Dragger customRequest={customUploadFile}
                                                                onChange={(info) => FileChanged(`AllgAng__Unterlagen__${entry.id}__UN_Doc`, entry.id, "Unterlagen")}
                                                                accept="image/jpg,image/jpeg,image/png,.doc,.docx,.xls,.xlsx,.pdf">
                                                    <p className="ant-upload-drag-icon">
                                                        <UploadOutlined/>
                                                    </p>
                                                    <p className="ant-upload-text">Klicke oder ziehe Dateien hierhin, um
                                                        sie hochzuladen</p>
                                                    <p className="ant-upload-hint">Unterstützte Dateiformate: .jpg,
                                                        .png, .pdf, .doc(x), .xls(x)</p>
                                                </Upload.Dragger>
                                            </>
                                        ) : uploadForms.Unterlagen?.[entry.id] !== undefined ? (
                                            <>
                                                <div>
                                                    <Button icon={<ExportOutlined/>}
                                                            onClick={() => openS3File(uploadForms.Unterlagen[entry.id].file.url)}>{uploadForms.Unterlagen[entry.id].file.name}</Button>
                                                    <Button icon={<DeleteOutlined/>}
                                                            onClick={() => handleRemoveAttachment(`AllgAng__Unterlagen__${entry.id}__UN_Doc`, uploadForms.Unterlagen[entry.id].file.id)}>
                                                        Löschen
                                                    </Button>
                                                </div>
                                            </>
                                        ) : null}
                                    </Form.Item>
                                </Card>
                            ))}
                            {!unterlagenHasDraft(uploadForms) && (
                            <div className={`flex  justify-center sacherCenter1 ${isSwitchOn ? 'maxColor' : ''}`}>
                                <Button
                                    className={` ${isSwitchOn ? 'auftragBtn1' : 'auftragBtn'}  text-[10px] md:text-[14px] m-[20px] button_new_component rounded-[30px] border-none`}
                                    onClick={() => addCard("Unterlagen", true)} id={"button_new_component_Unterlagen"}>Unterlagen
                                    hinzufügen</Button>
                            </div>
                            )}
                        </div>
                        <div id="hinweise" className="scrollable-section">
                            <h3 className={`font-bold text-16px mb-[12px]  md:text-[20px] generalText ${isSwitchOn ? 'text-white' : 'text-black'}`}>Hinweise</h3>
                            <div className={`allgemeineClass ${isSwitchOn ? 'maxColor' : ''} `}>
                                <div
                                    className={`flex textOuter justify-start gap-[10px] ${isSwitchOn ? 'maxColor' : ''}`}>
                                    <Form.Item label={`Urheber/Haftung 1:`}
                                               name={`AllgAng__HinweiseUrheberHaftung1`}>
                                        <TextArea autoSize={true} className="textInput"
                                                  ref={(element) => handleRef('AllgAng__HinweiseUrheberHaftung1', element)}/>
                                    </Form.Item>

                                    <Button className="mt-[35px] md:mt-0 md:ml-[13px] ml-[20px] text-[#FF2E3B] fileIcon"
                                            htmlType="button" onMouseDown={(e) => e.preventDefault()} onClick={() => handleOpenTextbausteine('AllgAng__HinweiseUrheberHaftung1', 'Hinweise Urheber/Haftung 1', 'Hinweise Urheber/Haftung 1')}><FileTextOutlined/></Button>
                                </div>
                                <div
                                    className={`flex textOuter justify-start gap-[10px] ${isSwitchOn ? 'maxColor' : ''}`}>
                                    <Form.Item label={`Urheber/Haftung 2:`}
                                               name={`AllgAng__HinweiseUrheberHaftung2`}>
                                        <TextArea autoSize={true} className="textInput"
                                                  ref={(element) => handleRef('AllgAng__HinweiseUrheberHaftung2', element)}/>
                                    </Form.Item>
                                    <Button className="mt-[35px] md:mt-0 md:ml-[13px] ml-[20px] text-[#FF2E3B] fileIcon"
                                            htmlType="button" onMouseDown={(e) => e.preventDefault()} onClick={() => handleOpenTextbausteine('AllgAng__HinweiseUrheberHaftung2', 'Hinweise Urheber/Haftung 2', 'Hinweise Urheber/Haftung 2')}><FileTextOutlined/></Button>
                                </div>
                                <div
                                    className={`flex textOuter justify-start gap-[10px] ${isSwitchOn ? 'maxColor' : ''}`}>
                                    <Form.Item label={`Rechtsgrundlagen:`}
                                               name={`AllgAng__HinweiseRechtsgrundlagen`}>
                                        <TextArea autoSize={true} className="textInput"
                                                  ref={(element) => handleRef('AllgAng__HinweiseRechtsgrundlagen', element)}/>
                                    </Form.Item>


                                    <Button className="mt-[35px] md:mt-0 md:ml-[13px] ml-[20px] text-[#FF2E3B] fileIcon"
                                            htmlType="button" onMouseDown={(e) => e.preventDefault()} onClick={() => handleOpenTextbausteine('AllgAng__HinweiseRechtsgrundlagen', 'Hinweise Rechtsgrundlagen', 'Hinweise Rechtsgrundlagen')}><FileTextOutlined/></Button>
                                </div>
                                <div
                                    className={`flex textOuter justify-start gap-[10px] ${isSwitchOn ? 'maxColor' : ''}`}>
                                    <Form.Item label={`Literatur`}
                                               name={`AllgAng__HinweiseLiteratur`}>
                                        <TextArea autoSize={true} className="textInput"
                                                  ref={(element) => handleRef('AllgAng__HinweiseLiteratur', element)}/>
                                    </Form.Item>


                                    <Button className="mt-[35px] md:mt-0 md:ml-[13px] ml-[20px] text-[#FF2E3B] fileIcon"
                                            htmlType="button" onMouseDown={(e) => e.preventDefault()} onClick={() => handleOpenTextbausteine('AllgAng__HinweiseLiteratur', 'Hinweise Literatur', 'Hinweise Literatur')}><FileTextOutlined/></Button>
                                </div>
                                <div
                                    className={`flex textOuter justify-start gap-[10px] ${isSwitchOn ? 'maxColor' : ''}`}>
                                    <Form.Item label={`Marktdaten`}
                                               name={`AllgAng__Marktdaten`}>
                                        <TextArea autoSize={true} className="textInput"
                                                  ref={(element) => handleRef('AllgAng__Marktdaten', element)}/>
                                    </Form.Item>
                                </div>
                            </div>
                        </div>

                    </Form>
                )}
            </div>
        </div>
    );
};

const AllgemeineAngaben = () => (
    <GutachtenShell menuLinks={MENU_LINKS}>
        <FormContainer />
    </GutachtenShell>
);

export default AllgemeineAngaben;
